import { useEffect, useMemo } from 'react'

import { FlexColumn, FlexRow, spacingSM } from 'Utils/styles/spacing'
import { Checkbox } from 'Components/Inputs/Checkbox/Checkbox'
import { AppIconMap, AppIcons } from 'Utils/types/appIcons'
import { HoverIconCard } from 'Components/HoverIconCard'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { AddPurchaseFormValues } from 'Components/Modal/AddPurchaseModal/AddPurchaseForm'
import { CurrencyCode } from 'Utils/types/currencies'
import { numToCurrency } from 'Utils/helpers/currency'

type OngoingPurchaseCheckboxProps = {
  control: Control<AddPurchaseFormValues>
  setValue: UseFormSetValue<AddPurchaseFormValues>
  currencyCode: CurrencyCode
  amountMeetsThreshold: boolean
  activeOngoingPurchaseForProgram: boolean
  hasPendingOngoingPurchase: boolean
  purchaseAmountRequired: number
}
export const OngoingPurchaseCheckbox = ({
  control,
  setValue,
  currencyCode,
  amountMeetsThreshold,
  activeOngoingPurchaseForProgram,
  hasPendingOngoingPurchase,
  purchaseAmountRequired
}: OngoingPurchaseCheckboxProps) => {
  const ongoingPurchase = useWatch({ control, name: 'ongoingPurchase' })

  const disabled = useMemo(
    () => !amountMeetsThreshold || activeOngoingPurchaseForProgram || hasPendingOngoingPurchase,
    [amountMeetsThreshold, activeOngoingPurchaseForProgram, hasPendingOngoingPurchase]
  )

  useEffect(() => {
    if (disabled) setValue('ongoingPurchase', false)
  }, [disabled, setValue])

  return (
    <FlexRow gap={spacingSM}>
      <Checkbox
        disabled={disabled}
        labelText="Request ongoing reimbursements for excess amount"
        value={ongoingPurchase ?? false}
        onClick={() => setValue('ongoingPurchase', !ongoingPurchase)}
      />
      <HoverIconCard
        iconSrc={AppIconMap[AppIcons.InfoCircle]}
        altText={'Ongoing reimbursement requirement'}
      >
        <FlexColumn gap={spacingSM} style={{ maxWidth: 300 }}>
          <span>You may only have one ongoing purchase per program at a time.</span>
          {hasPendingOngoingPurchase && (
            <span>You already have a pending request for ongoing reimbursements.</span>
          )}
          {!amountMeetsThreshold && (
            <span>
              Purchase must be at least {numToCurrency(purchaseAmountRequired, currencyCode)} to
              request ongoing reimbursements.
            </span>
          )}
        </FlexColumn>
      </HoverIconCard>
    </FlexRow>
  )
}
